import { EwayFormModel } from '../../models/Delivery-Executive/DeliveryExecutive';

interface EwayLine {
  productName: string;
  hsnCode: string | number;
  quantity: string | number;
  qtyUnit: string;
  taxableAmount: string | number;
  gst?: string | number;
  cess?: string | number;
  cessNonAdvol?: string | number;
}
const units: Record<string, string> = { KG: 'KGS', KILOGRAM: 'KGS', KILOGRAMS: 'KGS',
  LITER: 'LTR', LITRE: 'LTR', LITRES: 'LTR', LITERS: 'LTR', G: 'GMS', GRAM: 'GMS', GRAMS: 'GMS',
  PC: 'PCS', PIECE: 'PCS', PIECES: 'PCS', ML: 'MLT' };
const round = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
const amount = (value: string | number | undefined, label: string) => {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n) || n < 0) throw new Error(`The challan has an invalid ${label}. Refresh its product details.`);
  return n;
};
// Match the API's narrow correction of purchase tax/base precision drift.
// This list is not a rate whitelist; other rates retain provider validation.
const gstRateReferences = [0.1, 0.25, 0.5, 3, 5, 12, 18, 28, 40];
const normalizeGstRate = (rate: number) => gstRateReferences.find(reference =>
  Math.abs(rate - reference) <= 0.0001 + Number.EPSILON * Math.max(1, reference)) ?? rate;

export function buildEwayBillPayload(form: EwayFormModel, items: EwayLine[], userGstin?: string) {
  if (!items.length) throw new Error('No eligible challan products are available for an E-Way Bill.');
  const intra = form.billFrom.stateGstCode === form.billTo.stateGstCode;
  const gstin = userGstin || form.billFrom.gstin?.substring(0, 15) || 'URP';
  const itemList = items.map(p => {
    const gst = normalizeGstRate(amount(p.gst, 'GST rate'));
    const unit = String(p.qtyUnit || '').trim().toUpperCase();
    const hsnCode = Number(p.hsnCode), quantity = amount(p.quantity, 'quantity');
    if (!Number.isSafeInteger(hsnCode) || hsnCode <= 0 || quantity <= 0)
      throw new Error('The challan has an invalid HSN or quantity. Correct its product details before generating an E-Way Bill.');
    return { productName: p.productName, productDesc: p.productName, hsnCode,
      quantity, qtyUnit: units[unit] || unit,
      taxableAmount: round(amount(p.taxableAmount, 'taxable amount')),
      cgstRate: intra ? gst / 2 : 0, sgstRate: intra ? gst / 2 : 0, igstRate: intra ? 0 : gst,
      cessRate: amount(p.cess, 'cess rate'), cessNonadvol: amount(p.cessNonAdvol, 'cess amount') };
  });
  const totalValue = round(itemList.reduce((n, p) => n + p.taxableAmount, 0));
  const cgstValue = round(itemList.reduce((n, p) => n + p.taxableAmount * p.cgstRate / 100, 0));
  const sgstValue = round(itemList.reduce((n, p) => n + p.taxableAmount * p.sgstRate / 100, 0));
  const igstValue = round(itemList.reduce((n, p) => n + p.taxableAmount * p.igstRate / 100, 0));
  const cessValue = round(itemList.reduce((n, p) => n + p.taxableAmount * p.cessRate / 100, 0));
  const cessNonAdvolValue = round(itemList.reduce((n, p) => n + p.cessNonadvol, 0));
  const date = (value: Date | string | null | undefined) => value ? new Date(value).toLocaleDateString('en-GB') : '';
  const transport = form.transport as EwayFormModel['transport'] & { transporterDocNo?: string; transporterDocDate?: Date | string };
  return {
    supplyType: form.supplyType, subSupplyType: form.subType, subSupplyDesc: form.supplyType, docType: form.docType,
    docNo: String(form.docNo), docDate: date(form.docDate), fromGstin: gstin, fromTrdName: form.billFrom.name,
    fromAddr1: form.billFrom.address, fromAddr2: form.billFrom.address, fromPlace: form.billFrom.place,
    actFromStateCode: form.billFrom.stateGstCode, fromPincode: Number(form.billFrom.pincode), fromStateCode: form.billFrom.stateGstCode,
    toGstin: form.billTo.gstin?.substring(0, 15) || '', toTrdName: form.billTo.name,
    toAddr1: form.shipTo.address, toAddr2: form.shipTo.address, toPlace: form.shipTo.place,
    toPincode: Number(form.shipTo.pincode), actToStateCode: form.billTo.stateGstCode, toStateCode: form.billTo.stateGstCode,
    transactionType: Number(form.transactionType), totalValue, cgstValue, sgstValue, igstValue, cessValue, cessNonAdvolValue,
    totInvValue: round(totalValue + cgstValue + sgstValue + igstValue + cessValue + cessNonAdvolValue),
    transMode: String(transport.mode || ''), transDistance: String(transport.distance || ''),
    transporterName: transport.transporterName, transporterId: transport.transporterId || null,
    transDocNo: transport.transporterDocNo || '', transDocDate: date(transport.transporterDocDate),
    vehicleNo: transport.vehicleNo, vehicleType: transport.vehicleType,
    dispatchFromGSTIN: gstin, dispatchFromTradeName: form.billFrom.name,
    shipToGSTIN: form.billTo.gstin, shipToTradeName: form.billTo.name, itemList
  };
}

const object = (value: unknown): Record<string, unknown> | undefined => {
  if (typeof value === 'string') { try { return object(JSON.parse(value)); } catch { return undefined; } }
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : undefined;
};
const safeMessage = (message: unknown, fallback: string): string =>
  typeof message === 'string' && message.length > 0 && message.length < 600 && !/[{}<>]/.test(message) &&
    !/token|secret|password|client[_ -]?id|api[_ -]?key|https?:\/\/|@|bearer|authorization|[A-Za-z0-9_+/=-]{40,}/i.test(message)
    ? message.replace(/\b[0-9]{2}[A-Z0-9]{13}\b/g, '[redacted]') : fallback;

export function readFetchEwayBillResponse(value: unknown): { isSuccess: boolean; message: string } {
  const response = object(value);
  if (typeof response?.isSuccess !== 'boolean')
    return { isSuccess: false, message: 'The fetch result could not be confirmed. Refresh the challan list and retry Fetch E-Way Bill.' };
  return { isSuccess: response.isSuccess, message: safeMessage(response.message, response.isSuccess
    ? 'E-Way Bill linked successfully.' : 'The E-Way Bill could not be fetched or linked. Verify the bill and challan numbers or contact support.') };
}

export function fetchEwayBillRequestError(failure: unknown): { isSuccess: false; message: string } {
  const response = object(object(failure)?.response);
  return { isSuccess: false, message: response?.status === 401 || response?.status === 403
    ? 'You do not have access to fetch this E-Way Bill. Sign in again or contact your administrator.'
    : 'The E-Way Bill could not be fetched or linked. Refresh the challan list and retry Fetch E-Way Bill.' };
}
export function readEwayBillResponse(value: unknown) {
  const response = object(value);
  const data = object(response?.data);
  const error = object(response?.error);
  const errorValue = response?.error;
  const hasError = Array.isArray(errorValue) ? errorValue.length > 0 : error ? Object.keys(error).length > 0 :
    typeof errorValue === 'string' && errorValue.trim().length > 0;
  const failedStatus = [response?.status, response?.status_cd].some(status =>
    ['0', 'false', 'failed', 'failure', 'error'].includes(String(status).toLowerCase()));
  if (hasError || failedStatus || response?.isSuccess === false) {
    const message = error?.message || response?.message || response?.status_desc;
    throw new Error(safeMessage(message, 'The E-Way Bill request was rejected. Contact support with the challan number.'));
  }
  const number = data?.ewayBillNo || data?.ewbNo || response?.ewayBillNo || response?.ewbNo;
  if ((typeof number === 'string' || typeof number === 'number') && /^\d{1,15}$/.test(String(number)))
    return { data: { ...data, ewayBillNo: String(number), ewbNo: String(number) } };
  throw new Error('The E-Way Bill result could not be confirmed. Check existing bills for this challan before submitting again.');
}

export function ewayBillRequestError(failure: unknown): Error {
  const response = object(object(failure)?.response);
  if (!response && failure instanceof Error && !('request' in failure)) return failure;
  if (response?.status === 400) return new Error('The E-Way Bill request failed validation. Review the required document, address and transport fields.');
  if (response?.status === 401 || response?.status === 403) return new Error('You do not have access to generate this E-Way Bill. Sign in again or contact your administrator.');
  return new Error('The E-Way Bill service could not be reached. Check existing bills for this challan before submitting again.');
}
